import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

type AutoSizedColumnValue = boolean | number | string | null | undefined;

export interface AutoSizedColumnConfig<T extends object> {
	getValues: (row: T) => AutoSizedColumnValue | readonly AutoSizedColumnValue[];
	header?: string;
	includeHeader?: boolean;
	minSize?: number;
	maxSize?: number;
	pixelsPerChar?: number;
	padding?: number;
	splitBy?: string;
}

export interface UseAutoSizedColumnsProps<T extends object> {
	columns: ColumnDef<T>[];
	data: T[];
	columnSettings: Partial<Record<string, AutoSizedColumnConfig<T>>>;
}

function getColumnId<T extends object>(column: ColumnDef<T>) {
	if (column.id) {
		return column.id;
	}

	return 'accessorKey' in column && typeof column.accessorKey === 'string'
		? column.accessorKey
		: undefined;
}

function getNormalizedValues(
	value: AutoSizedColumnValue | readonly AutoSizedColumnValue[]
) {
	return Array.isArray(value) ? value : [value];
}

function getLongestSegmentLength(value: string, splitBy: string) {
	if (!splitBy) {
		return value.length;
	}

	let maxSegmentLength = 0;

	for (const segment of value.split(splitBy)) {
		maxSegmentLength = Math.max(maxSegmentLength, segment.length);
	}

	return maxSegmentLength;
}

export function useAutoSizedColumns<T extends object>({
	columns,
	data,
	columnSettings,
}: UseAutoSizedColumnsProps<T>) {
	return useMemo(
		() =>
			columns.map((column) => {
				const columnId = getColumnId(column);
				if (!columnId) {
					return column;
				}

				const config = columnSettings[columnId];
				if (!config) {
					return column;
				}

				const minSize = config.minSize ?? column.minSize ?? 30;
				const maxSize = config.maxSize ?? column.maxSize ?? 800;
				const padding = config.padding ?? 32;
				const pixelsPerChar = config.pixelsPerChar ?? 7;
				const splitBy = config.splitBy ?? '';
				const header =
					config.includeHeader === false
						? ''
						: (config.header ??
							(typeof column.meta?.name === 'string' ? column.meta.name : ''));

				let maxContentLength = header.length;

				for (const row of data) {
					for (const value of getNormalizedValues(config.getValues(row))) {
						if (value === null || value === undefined || value === '') {
							continue;
						}

						maxContentLength = Math.max(
							maxContentLength,
							getLongestSegmentLength(String(value), splitBy)
						);
					}
				}

				return {
					...column,
					size: Math.min(
						maxSize,
						Math.max(
							minSize,
							Math.ceil(maxContentLength * pixelsPerChar + padding)
						)
					),
					minSize,
					maxSize,
				};
			}),
		[columnSettings, columns, data]
	);
}
